import { computed, type ComputedRef } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getArticleParentConfig } from '@/common/api/api.pleased';
import type { ConnectToAgent } from '@/common/bot/types';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { storeToRefs } from 'pinia';

/**
 * TanStack Query hook to fetch article parent config (category/section config)
 * This config contains the connect-to-agent settings inherited from parent category/section
 *
 * @param articleId - Article ID to fetch parent config for
 * @returns TanStack Query result with parent config data
 *
 * @example
 * ```ts
 * const articleId = computed(() => Number(route.params.id));
 * const { data: parentConfig } = useArticleParentConfig(articleId);
 * ```
 */
export const useArticleParentConfig = (articleId: ComputedRef<string | number | undefined>) => {
  const mockedFlowStore = useMockedFlowStore();
  const { isActive: isMockedFlowActive } = storeToRefs(mockedFlowStore);

  return useQuery({
    queryKey: ['articleParentConfig', articleId],
    queryFn: async () => {
      if (isMockedFlowActive.value) return null;

      if (!articleId.value) {
        throw new Error('Article ID is required');
      }

      const response = await getArticleParentConfig.cached({
        articleId: articleId.value,
        getCategory: true,
      });

      return response.data;
    },
    enabled: computed(() => !!articleId.value),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    retry: true,
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 60_000),
  });
};
